import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const Contact = ({ business, mapEmbed }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/contact", form);
      toast.success(res.data.message || "Message sent!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-black border border-white/15 px-4 py-3.5 text-white placeholder:text-white/30 focus:border-ad-red focus:outline-none transition-colors";

  return (
    <section id="contact" className="relative py-24 sm:py-32 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="text-ad-red text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-ad-red" /> Visit The Showroom
          </p>
          <h2 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] tracking-tight">
            Come See It <span className="metallic-text">In Person</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Info + map */}
          <div className="flex flex-col gap-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoCard icon={<MapPin size={20} />} title="Address">
                <a
                  data-testid="contact-address"
                  href={`https://www.google.com/maps?q=${encodeURIComponent(business.address)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-ad-red transition-colors"
                >
                  {business.address}
                </a>
              </InfoCard>
              <InfoCard icon={<Phone size={20} />} title="Call Us">
                <div className="flex flex-col gap-1">
                  {business.phones?.map((p) => (
                    <a
                      key={p}
                      data-testid={`contact-phone-${p}`}
                      href={`tel:${p.replace(/[^0-9]/g, "")}`}
                      className="hover:text-ad-red transition-colors"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </InfoCard>
              <InfoCard icon={<Clock size={20} />} title="Hours">
                <div className="flex flex-col gap-1 text-sm">
                  {business.hours?.map((h) => (
                    <div key={h.day} className="flex justify-between gap-3">
                      <span className="text-white/50">{h.day}</span>
                      <span>{h.time}</span>
                    </div>
                  ))}
                </div>
              </InfoCard>
              <InfoCard icon={<Mail size={20} />} title="Email">
                <a href={`mailto:${business.email}`} className="hover:text-ad-red transition-colors break-all">
                  {business.email}
                </a>
              </InfoCard>
            </div>

            <div className="border border-white/10 overflow-hidden h-[280px]">
              <iframe
                title="map"
                src={mapEmbed}
                className="w-full h-full grayscale contrast-125"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-ad-surface border border-white/10 p-7 sm:p-10 flex flex-col gap-4"
          >
            <h3 className="font-display text-3xl uppercase tracking-tight mb-2">Send Us A Message</h3>
            <input
              className={field}
              placeholder="Full Name"
              required
              data-testid="contact-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              className={field}
              type="email"
              placeholder="Email Address"
              required
              data-testid="contact-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className={field}
              placeholder="Phone (optional)"
              data-testid="contact-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <textarea
              className={field}
              rows={5}
              placeholder="What are you looking for?"
              required
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-2 flex items-center justify-center gap-2 px-8 py-4 bg-ad-red text-white font-bold uppercase tracking-widest text-sm hover:bg-ad-redhover transition-colors disabled:opacity-50"
            >
              <Send size={16} /> {loading ? "Sending..." : "Send Message"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const InfoCard = ({ icon, title, children }) => (
  <div className="bg-ad-surface border border-white/10 p-6 hover:border-ad-red/40 transition-colors">
    <div className="flex items-center gap-3 text-ad-red mb-3">
      {icon}
      <span className="text-xs font-bold uppercase tracking-widest text-white/70">{title}</span>
    </div>
    <div className="text-white/90">{children}</div>
  </div>
);
